import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	useTheme,
	ListItemText,
	useMediaQuery,
	TableHead,
	TableRow,
	TableCell,
	Button,
	Box,
	LinearProgress,
	CircularProgress,
	IconButton,
	Typography,
} from '@mui/material';

import { Face as Avatar } from '@mui/icons-material';

import ResponsiveTable from 'components/ResponsiveTable';

import { rowsPerPageOptions } from 'config/constants';

import { Link, useNavigate } from 'react-router-dom';

import { sendAccessByEmail } from 'lib/models/access';
import { enqueueSnackbarError, enqueueSnackbarSuccess } from 'lib/helpers';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

type Props = {
	loading?: boolean;
	access: Access[];
	handleChangePage: (_: unknown, newPage: number) => any;
	handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => any;
	page: number;
	rowsPerPage: number;
	handleParams: (value: any, key: string) => void;
	creationOrder: string;
};

export default function AccessTable({
	loading,
	access,
	handleChangePage,
	handleChangeRowsPerPage,
	page,
	rowsPerPage,
	creationOrder,
	handleParams,
}: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	const [selected, setSelected] = useState<Access | null>(null);

	const onDetail = (selected: Access) => {
		const { code } = selected;
		navigate(`/access/${code}`);
	};
	const onSendEmail = async (acc: Access) => {
		setSelected(acc);
		try {
			const { success } = await sendAccessByEmail(acc.code);

			if (success) {
				enqueueSnackbarSuccess(`Email enviado a ${acc.email}`);
			}
		} catch (error) {
			console.error(error);
			enqueueSnackbarError('Hubo un error al enviar el mail');
		} finally {
			setSelected(null);
		}
	};

	const handleCreatedAtSort = (currentValue: string) => {
		const sort = currentValue === 'any' ? 'asc' : currentValue === 'asc' ? 'desc' : 'any';
		handleParams(sort, 'createdAt');
	};

	return (
		<Box sx={{ maxHeight: '50vh' }}>
			{!!loading && <LinearProgress />}

			<ResponsiveTable
				elements={isMobile ? access : access.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
				list={{
					primaryKey: '_id',
					avatar: <Avatar />,
					renderListItemText: (acc) => {
						return (
							<ListItemText
								id={acc._id}
								onClick={() => onDetail(acc)}
								primary={`${acc.code}`}
								secondary={
									<Box>
										<address>
											<a href={`mailto:${acc.code}`} target="_blank" rel="noreferrer">
												{acc.code}
											</a>
										</address>
									</Box>
								}
							/>
						);
					},
				}}
				table={{
					renderHead: () => (
						<TableHead>
							<TableRow>
								<TableCell align="left" padding="normal">
									{t('access:code')}
								</TableCell>
								<TableCell align="left" padding="normal">
									{t('access:type')}
								</TableCell>
								<TableCell align="left" padding="normal">
									Nro ID
								</TableCell>
								<TableCell align="left" padding="normal">
									Descripción
								</TableCell>
								<TableCell align="left" padding="normal">
									{t('access:plate')}
								</TableCell>
								<TableCell align="left" padding="normal">
									Fecha desde
								</TableCell>
								<TableCell align="left" padding="normal">
									Fecha hasta
								</TableCell>
								<TableCell align="left" padding="normal">
									<Box sx={{ display: 'flex', alignItems: 'center' }}>
										<Typography>Fecha creación</Typography>
										<IconButton
											size="small"
											onClick={() => {
												handleCreatedAtSort(creationOrder);
											}}
										>
											{creationOrder === 'any' ? (
												<ArrowUpwardIcon />
											) : creationOrder === 'asc' ? (
												<ArrowUpwardIcon color="primary" />
											) : (
												<ArrowDownwardIcon color="primary" />
											)}
										</IconButton>
									</Box>
								</TableCell>

								<TableCell colSpan={3} align="left">
									{t('common:actions')}
								</TableCell>
							</TableRow>
						</TableHead>
					),
					renderRow: (acc) => {
						// const isMultiple = acc.qrType === 'multiple';
						return (
							<TableRow tabIndex={-1} key={acc._id}>
								<TableCell>{acc.code}</TableCell>
								<TableCell>{typeof acc.type !== 'string' ? acc.type.description : '-'}</TableCell>
								<TableCell>{acc.tent || '-'}</TableCell>
								<TableCell>{acc.description || '-'}</TableCell>
								<TableCell>{acc.plate.join(' | ') || '-'}</TableCell>
								<TableCell>{parseDate(acc.validDate.from)}</TableCell>
								<TableCell>{parseDate(acc.validDate.to)}</TableCell>
								<TableCell>{parseDate(acc.createdAt)}</TableCell>
								<TableCell colSpan={3}>
									<Box sx={{ display: 'flex', alignItems: 'center' }}>
										<Box>
											<Button sx={{ marginRight: 1 }} onClick={() => onDetail(acc)} variant="outlined">
												Detalle
											</Button>
										</Box>
										{acc.qrType === 'multiple' && (
											<Box>
												<Button
													sx={{ marginRight: 1 }}
													component={Link}
													to={`/generate?access=${acc.code}`}
													variant="outlined"
												>
													Editar
												</Button>
											</Box>
										)}
										{acc.email && (
											<Box>
												<Button onClick={() => onSendEmail(acc)} variant="outlined">
													{selected ? <CircularProgress /> : 'Enviar mail'}
												</Button>
											</Box>
										)}
									</Box>
								</TableCell>
							</TableRow>
						);
					},
					pagination: {
						count: access.length,
						page,
						rowsPerPage,
						rowsPerPageOptions,
						onPageChange: handleChangePage,
						onRowsPerPageChange: handleChangeRowsPerPage,
					},
				}}
			/>
		</Box>
	);
}

function parseDate(dateString?: string) {
	if (dateString) {
		const date = new Date(dateString);
		const day = String(date.getDate()).padStart(2, '0');
		const month = months[date.getMonth()];
		const year = String(date.getFullYear()).slice(-2);

		return `${day}-${month}-${year}`;
	}
	return '-';
}

const months: { [key: number]: string } = {
	0: 'ene',
	1: 'feb',
	2: 'mar',
	3: 'abr',
	4: 'may',
	5: 'jun',
	6: 'jul',
	7: 'ago',
	8: 'sep',
	9: 'oct',
	10: 'nov',
	11: 'dic',
};
